import type {
  FooterLink,
  HeaderNavItem,
  NavItem as SidebarNavItem,
} from '@purolator/ui-kit-angular';
import {
  SIDEBAR_CLOCK_ICON,
  SIDEBAR_LOCATION_ICON,
  SIDEBAR_START_SHIPPING_ICON,
  SIDEBAR_TRACK_SHIPMENTS_ICON,
} from './sidebar-kit-icons';

/** Side-menu item ids that open Purolator tools outside ESO Lite (OM-825). */
export type SideMenuExternalNavId =
  'rate-zone-guide' | 'get-estimates' | 'track-shipments' | 'locations';

/**
 * Locale-specific destination URLs for the four OM-825 side-menu tools.
 * English URLs match the confirmed OM-825 table; French paths follow existing app/site mirrors.
 */
export const SIDE_MENU_EXTERNAL_URLS = {
  en: {
    'rate-zone-guide': 'https://www.purolator.com/en/shipping-rates-zones-service-guides',
    'get-estimates': 'https://public.purolator.com/estimate?lang=en',
    'track-shipments': 'https://www.purolator.com/en/shipping/tracker',
    locations: 'https://www.purolator.com/en/shipping/find-shipping-centre',
  },
  fr: {
    'rate-zone-guide': 'https://www.purolator.com/fr/shipping-rates-zones-service-guides',
    'get-estimates': 'https://public.purolator.com/estimate?lang=fr',
    'track-shipments': 'https://www.purolator.com/fr/expedition/faire-le-suivi-dun-envoi',
    locations: 'https://www.purolator.com/fr/expedition/trouver-un-centre-dexpedition',
  },
} as const satisfies Record<'en' | 'fr', Record<SideMenuExternalNavId, string>>;

/**
 * Builds the top header navigation items from translated labels.
 * OM-825: only Shipping remains after Vision, API, and Support are removed.
 * @param translate Resolves a layout translation key to localized text.
 */
export function buildHeaderNavItems(translate: (key: string) => string): HeaderNavItem[] {
  return [{ label: translate('layout.header.shipping'), href: '#', active: true }];
}

/**
 * Returns true when the current router URL is part of the Create A Shipment wizard
 * (Ship From/Ship To, Package, Service, Review & Pay, or Get Labels step).
 */
export function isCreateShipmentRoute(currentPath: string): boolean {
  const normalized = currentPath.split('?')[0]?.split('#')[0] ?? '/';
  return (
    normalized === '/' ||
    normalized === '' ||
    normalized === '/package' ||
    normalized === '/service' ||
    normalized === '/review' ||
    normalized === '/get-labels' ||
    normalized === '/cancel-shipment'
  );
}

/**
 * Resolves the absolute Purolator URL for an OM-825 side-menu external item.
 * @param itemId Sidebar/mobile nav item id.
 * @param locale Active UI locale used to select EN/FR destinations.
 * @returns Absolute URL, or null when the id is not an external side-menu tool.
 */
export function resolveSideMenuExternalUrl(itemId: string, locale: 'en' | 'fr'): string | null {
  const urls = SIDE_MENU_EXTERNAL_URLS[locale];
  if (itemId in urls) {
    return urls[itemId as SideMenuExternalNavId];
  }
  return null;
}

/**
 * Opens an absolute URL in a new browser tab without leaving the ESO Lite session.
 * Shared by footer legal links (OM-798) and side-menu tools (OM-825).
 * @param url Absolute destination URL.
 */
export function openExternalUrlInNewTab(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Opens the OM-825 side-menu destination for `itemId` in a new tab when it is external.
 * @param itemId Sidebar nav item id emitted by puro-sidebar-navigation.
 * @param locale Active UI locale used to select EN/FR destinations.
 * @returns True when an external URL was opened; false for internal items (e.g. create-shipment).
 */
export function openSideMenuExternalUrl(itemId: string, locale: 'en' | 'fr'): boolean {
  const url = resolveSideMenuExternalUrl(itemId, locale);
  if (!url) {
    return false;
  }
  openExternalUrlInNewTab(url);
  return true;
}

/**
 * Builds an external HeaderNavItem that opens in a new tab (OM-825).
 * Kits desktop header honors onClick; mobile drawer uses href + shell click interception.
 * @param label Localized link label.
 * @param url Absolute Purolator destination.
 * @param icon Optional SVG icon string for the mobile drawer.
 */
function buildExternalHeaderNavItem(label: string, url: string, icon?: string): HeaderNavItem {
  return {
    label,
    href: url,
    icon,
    onClick: (event: Event) => {
      event.preventDefault();
      openExternalUrlInNewTab(url);
    },
  };
}

/**
 * Builds the sidebar navigation tree from translated labels.
 * External OM-825 destinations are opened via itemClick + openSideMenuExternalUrl (kit buttons).
 * @param translate Resolves a layout translation key to localized text.
 * @param currentPath Active Angular router URL used to mark the create-shipment item active.
 * @param _locale Reserved for callers that rebuild when locale changes (URLs resolved on click).
 */
export function buildSidebarNavItems(
  translate: (key: string) => string,
  currentPath = '/',
  _locale: 'en' | 'fr' = 'en',
): SidebarNavItem[] {
  const createShipmentActive = isCreateShipmentRoute(currentPath);

  return [
    {
      id: 'start-shipping',
      label: translate('layout.sidebar.startShipping'),
      icon: SIDEBAR_START_SHIPPING_ICON,
      expanded: true,
      children: [
        {
          id: 'create-shipment',
          label: translate('layout.sidebar.createShipment'),
          href: '/',
          active: createShipmentActive,
        },
        {
          id: 'rate-zone-guide',
          label: translate('layout.sidebar.rateZoneGuide'),
        },
      ],
    },
    {
      id: 'get-estimates',
      label: translate('layout.sidebar.getEstimates'),
      icon: SIDEBAR_CLOCK_ICON,
    },
    {
      id: 'track-shipments',
      label: translate('layout.sidebar.trackShipments'),
      icon: SIDEBAR_TRACK_SHIPMENTS_ICON,
    },
    {
      id: 'locations',
      label: translate('layout.sidebar.locations'),
      icon: SIDEBAR_LOCATION_ICON,
    },
  ];
}

/**
 * Builds the compact (mobile) header navigation shown in the hamburger drawer.
 * OM-825: shipping tools with confirmed destinations; Vision/API/Support removed.
 * @param translate Resolves a layout translation key to localized text.
 * @param currentPath Active Angular router URL used to mark the create-shipment item active.
 * @param locale Active UI locale used to select EN/FR external hrefs.
 */
export function buildMobileNavItems(
  translate: (key: string) => string,
  currentPath = '/',
  locale: 'en' | 'fr' = 'en',
): HeaderNavItem[] {
  const createShipmentActive = isCreateShipmentRoute(currentPath);
  const urls = SIDE_MENU_EXTERNAL_URLS[locale];

  return [
    {
      label: translate('layout.sidebar.startShipping'),
      icon: SIDEBAR_START_SHIPPING_ICON,
      children: [
        {
          label: translate('layout.sidebar.createShipment'),
          href: '/',
          active: createShipmentActive,
        },
        buildExternalHeaderNavItem(
          translate('layout.sidebar.rateZoneGuide'),
          urls['rate-zone-guide'],
        ),
      ],
    },
    buildExternalHeaderNavItem(
      translate('layout.sidebar.getEstimates'),
      urls['get-estimates'],
      SIDEBAR_CLOCK_ICON,
    ),
    buildExternalHeaderNavItem(
      translate('layout.sidebar.trackShipments'),
      urls['track-shipments'],
      SIDEBAR_TRACK_SHIPMENTS_ICON,
    ),
    buildExternalHeaderNavItem(
      translate('layout.sidebar.locations'),
      urls.locations,
      SIDEBAR_LOCATION_ICON,
    ),
  ];
}

/**
 * Locale-specific Purolator.com legal page URLs for the shell footer (OM-798).
 * Paths match ESN guest footer + SERVICE_GUARANTEES_TERMS_URLS (Terms of Service).
 */
export const FOOTER_LEGAL_URLS = {
  en: {
    termsService: 'https://www.purolator.com/en/terms-and-conditions-service',
    termsSiteUse: 'https://www.purolator.com/en/terms-and-conditions-site-use',
    privacyPolicy: 'https://www.purolator.com/en/privacy',
  },
  fr: {
    termsService: 'https://www.purolator.com/fr/modalites-et-conditions-de-service',
    termsSiteUse: 'https://www.purolator.com/fr/modalites-et-conditions-dutilisation-du-site',
    privacyPolicy: 'https://www.purolator.com/fr/politique-de-confidentialite',
  },
} as const;

/**
 * Opens a footer legal URL in a new browser tab (OM-798 QA: same-tab navigates away from ESO Lite).
 * Uses the kit FooterLink.onClick hook because puro-footer does not support target="_blank".
 * @param url Absolute Purolator.com legal page URL.
 */
export function openFooterLegalUrl(url: string): void {
  openExternalUrlInNewTab(url);
}

/**
 * Builds the footer legal links from translated labels and locale-specific Purolator URLs.
 * Each link keeps href for accessibility and uses onClick to open in a new tab.
 * @param translate Resolves a layout translation key to localized text.
 * @param locale Active UI locale used to select EN/FR legal page hrefs.
 */
export function buildFooterLinks(
  translate: (key: string) => string,
  locale: 'en' | 'fr',
): FooterLink[] {
  const urls = FOOTER_LEGAL_URLS[locale];
  return [
    {
      label: translate('layout.footer.termsService'),
      href: urls.termsService,
      onClick: () => openFooterLegalUrl(urls.termsService),
    },
    {
      label: translate('layout.footer.termsSiteUse'),
      href: urls.termsSiteUse,
      onClick: () => openFooterLegalUrl(urls.termsSiteUse),
    },
    {
      label: translate('layout.footer.privacyPolicy'),
      href: urls.privacyPolicy,
      onClick: () => openFooterLegalUrl(urls.privacyPolicy),
    },
  ];
}

/**
 * Returns the short label for the alternate locale shown in the header toggle.
 * @param locale Active UI locale.
 */
export function alternateLocaleLabel(locale: 'en' | 'fr'): string {
  return locale === 'en' ? 'FR' : 'EN';
}

/**
 * Returns the target locale when the header language toggle is activated.
 * @param locale Active UI locale.
 */
export function alternateLocale(locale: 'en' | 'fr'): 'en' | 'fr' {
  return locale === 'en' ? 'fr' : 'en';
}
